import type { RegionCode } from '@developerz/domain';
import { Logo } from '@developerz/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { apiFetch } from '../lib/api';
import { browserRegionPrefill, regionOptions } from '../lib/region';
import type { ConsentReread } from './ConsentGate';
import { Field } from './Field';
import { Select } from './Select';
import { Button } from './ui/Button';

// The one-time region question (#5881). <App/> renders it after the ToS wall for a
// person whose `/v1/me` `user.region` is still null, and never again once they
// answer. It is its own screen, never folded into the ToS checkbox: the answer
// decides whether the separate cross-border consent applies (`CN` only), and that
// consent may not be bundled with anything.
//
// The region is pre-selected from the browser's language (`lib/region.ts`:
// `zh-CN` / `zh-Hans*` pre-select mainland China; `zh`, `zh-TW`, `zh-HK` and
// `zh-Hant*` never do), and the person confirms it. Saving POSTs
// `/v1/me/region`, then asks the caller to re-read `/v1/me`; a re-read that
// leaves the question up hands the button back, as <ConsentGate/> does.

export function RegionGate(props: {
  token: string | null;
  /** Called after a successful save: re-read `/v1/me`, answer whether it cleared. */
  onSaved: () => Promise<ConsentReread>;
  /** Injectable for tests; defaults to the browser language's pre-fill. */
  prefill?: RegionCode | null;
}): JSX.Element {
  const options = regionOptions(t.locale, t('consent.region_cn'));
  const [region, setRegion] = createSignal<RegionCode | ''>(
    (props.prefill === undefined ? browserRegionPrefill() : props.prefill) ?? '',
  );
  const [busy, setBusy] = createSignal(false);
  // 'save' = the POST failed; 'reload' = the re-read after a successful POST left
  // the gate up. The locale keys they map to are `consent.region_error` and
  // `consent.region_reload_error`. Keeping the literal as the cue rather than
  // as a locale-key suffix makes the union obvious from this line alone —
  // adding a third half (e.g. a 'sign-out' gate) is a new member and a new
  // JSX branch, not a misspelled string that maps to an unrelated message.
  const [failure, setFailure] = createSignal<'save' | 'reload' | null>(null);

  const save = async (): Promise<void> => {
    const chosen = region();
    if (chosen === '' || busy()) return;
    setBusy(true);
    setFailure(null);
    try {
      await apiFetch('/v1/me/region', props.token, {
        method: 'POST',
        body: JSON.stringify({ region: chosen }),
      });
    } catch {
      setFailure('save');
      setBusy(false);
      return;
    }
    // A re-read that THREW did not clear the question either, so it lands on the
    // same verdict as one answering `still_walled`. Unguarded, the rejection
    // escaped this handler: `busy` never reset and no failure ever painted, so
    // the screen sat on "Saving…" for good — no verdict on a region the POST
    // above had already stored, and no way back but a reload.
    let reread: ConsentReread;
    try {
      reread = await props.onSaved();
    } catch {
      reread = 'still_walled';
    }
    if (reread === 'still_walled') {
      setFailure('reload');
      setBusy(false);
    }
  };

  return (
    <main class="login">
      <div class="card login__card">
        <header class="card__header login__header">
          <span class="login__logo" aria-hidden="true">
            <Logo variant="icon" height={36} />
          </span>
          <h1 class="card__title login__title">{t('consent.region_title')}</h1>
          <p class="card__desc">{t('consent.region_body')}</p>
        </header>
        <div class="card__content login__content">
          <Show when={failure()}>
            {(why) => (
              <div class="alert alert--destructive" role="alert">
                <p class="alert__title">
                  {why() === 'save' ? t('consent.region_error') : t('consent.region_reload_error')}
                </p>
              </div>
            )}
          </Show>
          <Field id="region-gate-region" label={t('consent.region_label')}>
            {(a11y) => (
              <Select<RegionCode | ''>
                a11y={a11y}
                label={t('consent.region_label')}
                value={region()}
                options={options}
                onChange={setRegion}
                placeholder={t('consent.region_placeholder')}
                block
              />
            )}
          </Field>
          <Button disabled={region() === '' || busy()} onClick={save}>
            {busy() ? t('consent.region_saving') : t('consent.region_cta')}
          </Button>
        </div>
      </div>
    </main>
  );
}
